import React from 'react';

// The tab strip standard for every page with tabs (Jeremy, 2026-09-30 -- originated on
// Administration.js, then rolled out here so the rest of the app matches instead of inventing
// a fourth or fifth tab look).

/**
 * The tab named by the URL, or the first tab when the parameter is missing or nonsense.
 *
 * Falling back rather than erroring matters: `?tab=` comes from a link someone may have typed,
 * pasted or kept from before a tab was renamed, and a register page that renders nothing because of
 * a bad query string would look broken rather than wrong.
 */
export function activeTabKey(requested, tabs) {
  return tabs.some((t) => t.key === requested) ? requested : tabs[0].key;
}

/**
 * Folder tabs: rounded top corners, and the active one's bottom border painted the same color as
 * whatever sits directly beneath it (`panelBg`) so the two visually merge into one attached piece
 * instead of reading as two separate boxes with a gap.
 *
 * `merge` defaults to **false** -- only turn it on when the tab strip is immediately followed, in
 * *every* state the page can be in (loading, empty, error), by exactly one panel matching `panelBg`.
 * Get that wrong (a placeholder state with no panel, or several separate cards below instead of
 * one) and the active tab looks like it's missing its bottom edge, floating over nothing. Where a
 * page's content is one or more of its own separate `.card`s rather than a single matching panel,
 * leave `merge` off -- you still get the same tab shape, just with a normal, complete border on
 * every tab, active or not.
 *
 * `flexWrap` because four labels this long ("Billed-as-Used Register") exceed the content width
 * once the side nav is expanded on a narrower screen -- wrapping to two rows reads better than a
 * horizontally scrolling strip.
 */
export function TabBar({ tabs, active, onSelect, merge = false, panelBg = 'var(--surface)' }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: merge ? 0 : 20 }}>
      {tabs.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onSelect(t.key)}
            style={{
              fontSize: 13, fontWeight: 600, padding: '9px 18px', cursor: 'pointer',
              border: '1px solid var(--border)',
              borderBottom: isActive && merge ? `1px solid ${panelBg}` : '1px solid var(--border)',
              borderTopLeftRadius: 'var(--radius)', borderTopRightRadius: 'var(--radius)',
              borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
              marginRight: 4, marginBottom: -1,
              background: isActive ? panelBg : 'var(--light-bg)',
              color: isActive ? 'var(--text-dark)' : 'var(--text-mid)',
              position: 'relative', zIndex: isActive ? 2 : 1,
            }}
            aria-pressed={isActive}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
